import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  Typography
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Grid from "@mui/material/Unstable_Grid2";
import NextIcon from "@mui/icons-material/ArrowForwardIos";
import { useEffect, useRef, useState } from "react";
import {
  MixpanelEvents,
  baseCreateAssessmentSteps,
  createAssessmentButtonText,
  createAssessmentNotifications,
  createAssessmentStepsWConfirmProgram,
  assessmentNotes
} from "../../../app/constants";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { selectCurrentUser } from "../../Users/usersSlice";
import { useAPI, usersActionableRoleInProgram } from "../../../app/utils";
import { APIError } from "../../../common-ui/APIError";
import { User, AssessmentRole } from "../../../API";
import { ParticipantSelect } from "./ParticipantSelect";
import { ActivitySelect } from "./ActivitySelect";
import { LoadingButton } from "@mui/lab";
import { DateTimeSelect } from "./DateTimeSelect";
import { useNavigate } from "react-router-dom";
import { PageWrapper, PageWrapperProps } from "../../../common-ui/PageWrapper";
import { useDispatch } from "react-redux";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../../common-ui/toastSlice";
import { useBlockerWithConfirmation } from "../../../common-ui/ConfirmNav";
import { Mixpanel } from "../../Analytics/Mixpanel";
import { SponsorSelect } from "./SponsorSelect";
import { CategorySelect } from "./CategorySelect";
import { selectFeatureFlags } from "../../Firebase/firebaseSlice";
import {
  AvailableParticipant,
  AvailableParticipantsInput,
  fetchUser,
  availableParticipantsAPI
} from "../../Users/usersAPI";
import {
  AssessmentRequestReturn,
  AssessmentSummary,
  assessmentSummaryAPI,
  AssessmentSummaryInput,
  AssessmentSummaryProgram,
  availableActivitiesAPI,
  AvailableActivitiesInput,
  AvailableActivity,
  AvailableActivityType,
  availableActivityTypesAPI,
  AvailableActivityTypesInput,
  availableCategoriesAPI,
  AvailableCategoriesInput,
  AvailableCategory,
  requestAssessmentAPI,
  RequestAssessmentInput
} from "../../Activities/activitiesAPI";
import { ProgramSelect } from "./ProgramSelect";

export type SubjectLike = User | AvailableParticipant;

export function getUserId(user: SubjectLike): string {
  return "userId" in user ? user.userId : user.id;
}

export type ActivityPreview = {
  id?: string;
};

const pageDetails: PageWrapperProps = {
  className: "CreateAssessmentEvent"
};

export type CreateAssessmentEventProps = {
  role: AssessmentRole;
};

const getNonUndefinedActivities = (
  selectedActivities: (AvailableActivity | undefined)[]
) => {
  return selectedActivities.flatMap((activity) => (activity ? [activity] : []));
};

export const CreateAssessmentEvent = ({
  role: currentUsersAssessmentRole
}: CreateAssessmentEventProps) => {
  const navigate = useNavigate();
  const lastAssessmentSummaryRequestKey = useRef<string>("");

  //the below console might be needed for debugging purposes in the future, so leaving it here for now
  //console.log("role", currentUsersAssessmentRole);

  const dispatch = useDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const defaultDate = new Date();
  const featureFlags = useAppSelector(selectFeatureFlags);

  const [selectedParticipant, setSelectedParticipant] =
    useState<AvailableParticipant>();
  const [selectedActivities, setSelectedActivities] = useState<
    (AvailableActivity | undefined)[]
  >([undefined]);
  const [selectedActivityType, setSelectedActivityType] =
    useState<AvailableActivityType>();
  const [selectedCategory, setSelectedCategory] = useState<AvailableCategory>();
  const nonUndefinedActivities = selectedActivities.flatMap((activity) =>
    activity ? [activity] : []
  );

  const allCreatedActivitiesPopulated = selectedActivities.every(
    (selectedActivity) => selectedActivity
  );
  const [selectedActivityEventDate, setSelectedActivityEventDate] =
    useState<Date>(defaultDate);
  const [subjectCandidate, setSubjectCandidate] = useState<SubjectLike>();
  const [raterCandidate, setRaterCandidate] = useState<SubjectLike>();
  const [selectedProgram, setSelectedProgram] =
    useState<AssessmentSummaryProgram>();
  const [participantQuery, setParticipantQuery] = useState<string>("");
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const [activitiesQuery, setActivitiesQuery] = useState<string>("");
  const [createText, setCreateText] = useState(
    createAssessmentButtonText.CreateAndStart
  );
  const [availablePrograms, setAvailablePrograms] =
    useState<AssessmentSummaryProgram[]>();
  const [availableParticipantsInput, setAvailableParticipantsInput] =
    useState<AvailableParticipantsInput>();
  const [availableActivitiesInput, setAvailableActivitiesInput] =
    useState<AvailableActivitiesInput>();
  const [availableActivityTypesInput, setAvailableActivityTypesInput] =
    useState<AvailableActivityTypesInput>();
  const [availableCategoriesInput, setAvailableCategoriesInput] =
    useState<AvailableCategoriesInput>();
  const [assessmentSummaryInput, setAssessmentSummaryInput] =
    useState<AssessmentSummaryInput>();

  let participantAssessmentRole = "Participant";
  if (currentUsersAssessmentRole === AssessmentRole.rater) {
    participantAssessmentRole = "Subject";
  } else if (currentUsersAssessmentRole === AssessmentRole.subject) {
    participantAssessmentRole = "Rater";
  } else {
    participantAssessmentRole = "Participant";
  }

  const { t } = useTranslation();
  const {
    data: availableParticipants,
    status: availableParticipantsStatus,
    error: availableParticipantsError
  } = useAPI<AvailableParticipantsInput, AvailableParticipant[]>(
    availableParticipantsAPI,
    availableParticipantsInput
  );
  const {
    data: availableActivities,
    status: availableActivitiesStatus,
    error: availableActivitiesError
  } = useAPI<AvailableActivitiesInput, AvailableActivity[]>(
    availableActivitiesAPI,
    availableActivitiesInput
  );
  const {
    data: availableActivityTypes,
    status: availableActivityTypesStatus,
    error: availableActivityTypesError
  } = useAPI<AvailableActivityTypesInput, AvailableActivityType[]>(
    availableActivityTypesAPI,
    availableActivityTypesInput
  );
  const {
    data: availableCategories,
    status: availableCategoriesStatus,
    error: availableCategoriesError
  } = useAPI<AvailableCategoriesInput, AvailableCategory[]>(
    availableCategoriesAPI,
    availableCategoriesInput
  );
  const {
    data: assessmentSummary,
    status: assessmentSummaryStatus,
    error: assessmentSummaryError
  } = useAPI<AssessmentSummaryInput, AssessmentSummary>(
    assessmentSummaryAPI,
    assessmentSummaryInput
  );
  const {
    data: requestAssessmentResponse,
    status: requestAssessmentStatus,
    error: requestAssessmentError,
    execute: requestAssessment
  } = useAPI<RequestAssessmentInput, AssessmentRequestReturn>(
    requestAssessmentAPI
  );

  const [notesValue, setNotesValue] = useState<string>("");

  const handleNotesFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNotesValue(event.target.value);
  };

  const { activity_notes_visible } = featureFlags;

  const missingRequiredSponsorSelection =
    availableActivityTypes &&
    availableActivityTypes.length > 1 &&
    !selectedActivityType;

  const missingRequiredCategorySelection =
    availableCategories && availableCategories.length > 1 && !selectedCategory;

  const shouldBlock =
    !!(
      selectedParticipant ||
      (selectedActivities?.length > 0 && selectedActivities[0]) ||
      selectedActivityEventDate
    ) && requestAssessmentStatus !== "succeeded";
  useBlockerWithConfirmation({
    prompt: createAssessmentNotifications.NavPrompt,
    confirmText: createAssessmentNotifications.NavConfirm,
    cancelText: createAssessmentNotifications.NavCancel,
    shouldBlock: shouldBlock
  });

  const handleSubmit = () => {
    if (
      currentUser &&
      selectedProgram &&
      selectedActivityEventDate &&
      selectedParticipant
    ) {
      const subjectUserId =
        currentUsersAssessmentRole === AssessmentRole.subject
          ? currentUser.id
          : selectedParticipant.userId;
      const raterUserId =
        currentUsersAssessmentRole === AssessmentRole.rater
          ? currentUser.id
          : selectedParticipant.userId;

      const input: RequestAssessmentInput = {
        loggedInUserId: currentUser.id,
        participantUserIds: [selectedParticipant.userId],
        activityIds: getNonUndefinedActivities(selectedActivities).map(
          (activity) => activity.activityId
        ),
        programId: selectedProgram.id,
        activityEventDate: selectedActivityEventDate.toISOString(),
        notes: notesValue,
        subjectUserId: subjectUserId,
        raterUserId: raterUserId
      };
      requestAssessment(input);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      const input: AvailableParticipantsInput = {
        userId: currentUser.id,
        assessmentRole: currentUsersAssessmentRole,
        ...(participantQuery ? { query: participantQuery } : {})
      };
      setAvailableParticipantsInput(input);
    }
  }, [currentUser?.id, currentUsersAssessmentRole]);

  useEffect(() => {
    if (!selectedParticipant) {
      setSelectedActivities([undefined]);
      setSelectedProgram(undefined);
      setSelectedActivityType(undefined);
    }
  }, [selectedParticipant]);

  useEffect(() => {
    if (!currentUser?.id || !selectedParticipant || !currentUsersAssessmentRole)
      return;

    const subjectCandidate =
      currentUsersAssessmentRole === AssessmentRole.subject
        ? currentUser
        : selectedParticipant;
    const raterCandidate =
      currentUsersAssessmentRole === AssessmentRole.rater
        ? currentUser
        : selectedParticipant;

    setSubjectCandidate(subjectCandidate);
    setRaterCandidate(raterCandidate);

    // SE-1787: send the rater so the backend scopes each list to networks the
    // rater is allowed to rate the subject on (trainee-can-rate-trainee).
    const availableActivitiesInput: AvailableActivitiesInput = {
      subjectUserId: getUserId(subjectCandidate),
      raterUserId: getUserId(raterCandidate)
    };
    setAvailableActivitiesInput(availableActivitiesInput);
    const availableActivityTypesInput: AvailableActivityTypesInput = {
      subjectUserId: getUserId(subjectCandidate),
      raterUserId: getUserId(raterCandidate)
    };
    setAvailableActivityTypesInput(availableActivityTypesInput);
    const availableCategoriesInput: AvailableCategoriesInput = {
      subjectUserId: getUserId(subjectCandidate),
      raterUserId: getUserId(raterCandidate)
    };
    setAvailableCategoriesInput(availableCategoriesInput);
  }, [currentUser?.id, selectedParticipant, currentUsersAssessmentRole]);

  useEffect(() => {
    if (selectedActivityType) {
      if (availableActivitiesInput) {
        const newAvailableActivitiesInput: AvailableActivitiesInput = {
          ...availableActivitiesInput,
          sponsorId: selectedActivityType.sponsorId
        };
        setAvailableActivitiesInput(newAvailableActivitiesInput);
      }
      if (availableCategoriesInput) {
        const newAvailableCategoriesInput: AvailableCategoriesInput = {
          ...availableCategoriesInput,
          sponsorId: selectedActivityType.sponsorId
        };
        setAvailableCategoriesInput(newAvailableCategoriesInput);
      }
    }
    // Dev note: if we see strange behavior with the sponsor select, we may need to add availableActivitiesInput as a dependency here.
  }, [selectedActivityType]);

  useEffect(() => {
    if (selectedCategory) {
      if (availableActivitiesInput) {
        const newAvailableActivitiesInput: AvailableActivitiesInput = {
          ...availableActivitiesInput,
          categoryId: selectedCategory.categoryId
        };
        if (selectedCategory.categoryId === "all") {
          delete newAvailableActivitiesInput.categoryId;
        }
        setAvailableActivitiesInput(newAvailableActivitiesInput);
      }
    }
    setSelectedActivities([undefined]);
    setActivitiesQuery("");
    // Dev note: if we see strange behavior with the category select, we may need to add availableActivitiesInput as a dependency here.
  }, [selectedCategory]);

  useEffect(() => {
    setAvailableActivitiesInput((prev) => {
      if (!prev) return;
      return {
        ...prev,
        query: activitiesQuery
      };
    });
  }, [activitiesQuery]);

  useEffect(() => {
    setAvailableParticipantsInput((prev) => {
      if (!prev) return;
      return {
        ...prev,
        query: participantQuery
      };
    });
  }, [participantQuery]);

  useEffect(() => {
    const summaryPrograms = assessmentSummary?.programs;
    if (summaryPrograms) {
      if (summaryPrograms.length > 1) {
        setAvailablePrograms(summaryPrograms);
      }
      if (summaryPrograms.length === 1) {
        setSelectedProgram(summaryPrograms[0]);
      }
    }
  }, [assessmentSummary]);

  useEffect(() => {
    if (
      selectedProgram?.creationBehavior ===
      createAssessmentButtonText.CreateAndStart
    ) {
      setCreateText(createAssessmentButtonText.CreateAndStart);
    } else {
      setCreateText(createAssessmentButtonText.Create);
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (requestAssessmentStatus === "succeeded") {
      dispatch(
        setToastMessage(
          createAssessmentNotifications.AssessmentCreatedSuccessfully
        )
      );
      dispatch(setToastStyle("success"));
      dispatch(setShowToast(true));

      // We will be adding an additional action so that we have one for "Save"
      // and one for "Save & Start", but to match current mobile behavior,
      // we will use "Save & Start" for both.
      Mixpanel.track(MixpanelEvents.AssessmentCreatedAndStarted);

      // Route off the backend response, which is authoritative: any events the
      // logged-in user must complete means "start" (regardless of activity
      // count); none means it was a request-only creation. Deriving this from
      // the summary/selectedProgram could desync (e.g. after adding activities)
      // and leave the user stranded on this page.
      const assessmentEventsToComplete =
        requestAssessmentResponse?.assessmentEventsToComplete ?? [];
      if (!shouldBlock) {
        if (assessmentEventsToComplete.length > 0) {
          navigate(`/assessments/complete/${assessmentEventsToComplete[0]}`);
        } else {
          navigate("/assessments/pending");
        }
      }
    }
  }, [requestAssessmentStatus]);

  useEffect(() => {
    if (currentUser && selectedParticipant && selectedActivities?.length > 0) {
      if (!subjectCandidate || !raterCandidate) {
        return;
      }

      const nonUndefinedActivities = selectedActivities.flatMap((activity) =>
        activity ? [activity] : []
      );
      const activityIds = nonUndefinedActivities.map(
        (activity) => activity.activityId
      );
      const subjectUserId = getUserId(subjectCandidate);
      const raterUserId = getUserId(raterCandidate);

      // Avoid redundant assessment-summary calls when effective input is unchanged
      // (for example, adding an empty activity row without selecting an activity).
      const requestKey = JSON.stringify({
        subjectUserId,
        raterUserId,
        selectedUserId: selectedParticipant.userId,
        currentUsersAssessmentRole,
        activityIds: [...activityIds].sort()
      });
      if (lastAssessmentSummaryRequestKey.current === requestKey) {
        return;
      }
      lastAssessmentSummaryRequestKey.current = requestKey;

      const input: AssessmentSummaryInput = {
        subjectUserId,
        raterUserId,
        currentUsersAssessmentRole: currentUsersAssessmentRole,
        selectedUserId: selectedParticipant.userId,
        activityIds: activityIds
      };
      setAssessmentSummaryInput(input);
    }
  }, [selectedActivities]);

  return (
    <PageWrapper {...pageDetails}>
      <Grid container xs={12} mdOffset={2} md={8} p={2} spacing={2}>
        <Grid xs={12} display="flex" justifyContent="center">
          <Typography variant="h6">{t("Create New Assessment")}</Typography>
        </Grid>
        {availableActivitiesError && (
          <Grid xs={12}>
            <Alert severity="error">
              {t("Error fetching available activities.")}
            </Alert>
          </Grid>
        )}
        <Grid xs={12} md={8} mdOffset={2}>
          <ParticipantSelect
            availableParticipants={availableParticipants || []}
            participant={selectedParticipant}
            setParticipant={setSelectedParticipant}
            setParticipantQuery={setParticipantQuery}
            disabled={requestAssessmentStatus === "loading"}
            participantAssessmentRole={participantAssessmentRole}
            loading={availableParticipantsStatus === "loading"}
          />
        </Grid>
        {selectedParticipant &&
          availableActivityTypes &&
          availableActivityTypes.length > 1 && (
            <Grid xs={12} md={8} mdOffset={2}>
              <SponsorSelect
                availableActivityTypes={availableActivityTypes || []}
                activityType={selectedActivityType}
                setSelectedActivityType={setSelectedActivityType}
                disabled={requestAssessmentStatus === "loading"}
              />
            </Grid>
          )}
        {selectedParticipant &&
          ((availableCategories && availableCategories.length > 1) ||
            selectedCategory) &&
          !missingRequiredSponsorSelection && (
            <Grid xs={12} md={8} mdOffset={2}>
              <CategorySelect
                categories={availableCategories || []}
                category={selectedCategory}
                setCategory={setSelectedCategory}
                disabled={requestAssessmentStatus === "loading"}
              />
            </Grid>
          )}
        {selectedActivities.map((field, index) => {
          return (
            <Grid
              xs={12}
              md={8}
              mdOffset={2}
              key={`autocomplete-${!field ? "empty" : field?.activityId}`}
            >
              <ActivitySelect
                selectedParticipant={selectedParticipant}
                participantAssessmentRole={participantAssessmentRole}
                missingRequiredSponsorSelection={
                  !!missingRequiredSponsorSelection
                }
                loading={availableActivitiesStatus === "loading"}
                missingRequiredCategorySelection={
                  !!missingRequiredCategorySelection
                }
                sharedActivitiesStatus={availableActivitiesStatus}
                availableActivities={availableActivities || []}
                selectedActivities={selectedActivities}
                activity={field}
                activitiesQuery={activitiesQuery}
                setActivitiesQuery={setActivitiesQuery}
                setSelectedActivities={setSelectedActivities}
                index={index}
                disabled={
                  !selectedParticipant ||
                  !availableActivities ||
                  missingRequiredSponsorSelection ||
                  missingRequiredCategorySelection ||
                  requestAssessmentStatus === "loading"
                }
                deleteable={
                  selectedActivities?.length > 1 &&
                  requestAssessmentStatus !== "loading"
                }
              />
            </Grid>
          );
        })}
        {allCreatedActivitiesPopulated && (
          <Grid xs={12} display="flex" justifyContent="center">
            <Button
              color="primary"
              startIcon={<AddCircleIcon />}
              disabled={
                !allCreatedActivitiesPopulated ||
                requestAssessmentStatus === "loading"
              }
              onClick={() => {
                const clonedActivities = [...selectedActivities];
                clonedActivities.push(undefined);
                setSelectedActivities(clonedActivities);
                setActivitiesQuery("");
              }}
            >
              {t("Add Activity")}
            </Button>
          </Grid>
        )}
        <Grid xs={12} md={8} mdOffset={2}>
          <DateTimeSelect
            selectedDate={selectedActivityEventDate}
            setSelectedDate={setSelectedActivityEventDate}
            disabled={requestAssessmentStatus === "loading"}
          />
        </Grid>

        {/* The below block will be needed in the future when anyone can rate anyone
              and when the two users are part of multiple common programs.
              Currently, only Trainees can be subjects; and users can be Trainees only in one program.
          */}
        {/* {createAssessmentStepsArray ===
            createAssessmentStepsWConfirmProgram &&
            sharedPrograms && (
              <>
                <Grid xs={12} md={8} mdOffset={2}>
                  <ProgramSelect
                    selectedProgram={selectedProgram}
                    sharedPrograms={sharedPrograms}
                    institutions={institutions || undefined}
                    setSelectedProgram={setSelectedProgram}
                    disabled={
                      institutionsStatus !== "succeeded" ||
                      getAssessmentStatus === "loading" ||
                      submitAssessmentRequestStatus === "loading"
                    }
                  />
                </Grid>
                {institutionsError && (
                  <Grid xs={12} md={8} mdOffset={2}>
                    <APIError error={institutionsError} />
                  </Grid>
                )}
              </>
            )} */}
        {availablePrograms && (
          <>
            <Grid xs={12} md={8} mdOffset={2}>
              <ProgramSelect
                selectedProgram={selectedProgram}
                sharedPrograms={availablePrograms}
                setSelectedProgram={setSelectedProgram}
                disabled={requestAssessmentStatus === "loading"}
              />
            </Grid>
          </>
        )}
        {requestAssessmentError && (
          <Grid xs={12}>
            <Alert severity="error">
              {t("Error submitting assessment request.")}
            </Alert>
          </Grid>
        )}
        {activity_notes_visible && selectedProgram?.notes && (
          <Grid xs={12} md={8} mdOffset={2}>
            <Typography variant="subtitle1" gutterBottom>
              {t(selectedProgram.notes?.prompt)}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom
              dangerouslySetInnerHTML={{
                __html: t(selectedProgram?.notes?.helpText)
              }}
            />
            <TextField
              multiline
              rows={4}
              value={notesValue}
              onChange={handleNotesFieldChange}
              placeholder={t(assessmentNotes.AssessmentNotesPlaceholder)}
              fullWidth
              variant="outlined"
              disabled={requestAssessmentStatus === "loading"}
              inputProps={{
                maxLength: 512
              }}
            />
          </Grid>
        )}
        <Grid xs={12} container display="flex" justifyContent="center">
          <Grid>
            <LoadingButton
              onClick={handleSubmit}
              endIcon={<NextIcon />}
              variant={"contained"}
              loading={
                availableActivitiesStatus === "loading" ||
                requestAssessmentStatus === "loading"
              }
              disabled={
                availableActivitiesStatus === "loading" ||
                requestAssessmentStatus === "loading" ||
                !selectedParticipant ||
                !selectedProgram ||
                !selectedActivities ||
                !allCreatedActivitiesPopulated ||
                !selectedActivityEventDate ||
                !assessmentSummary ||
                assessmentSummaryStatus === "loading"
              }
            >
              {t(createText)}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </PageWrapper>
  );

  return null;
};

